import { useMemo, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { useSession } from '@/contexts/LitSessionProvider';
import useERC20Balance from '@/hooks/portfolio/useERC20Balance';
import { ACTIVE_NETWORKS, NETWORK_LOGOS } from '@shared/utils/config/networks';
import { V2Networks } from '@shared/utils/config/networkAddresses';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ImageWithFallback } from '@/components/shared/NextImageFallback';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Settings, ChevronLeft } from 'lucide-react';
enum QuickBuyStages {
  balances = 'balances',
  setUp = 'set up',
}
interface NetworkData {
  networkName: string;
  chainId: number;
  networkLogo: string;
  ticker: string;
}
interface QuickBuyTokensLocalStorage extends NetworkData {
  amount: string;
}
const TokenSetUp = ({
  token,
  setStage,
  onSetUpToken
}: {
  token: NetworkData & {
    balance: string | number;
  };
  setStage: (_: QuickBuyStages) => void;
  onSetUpToken: (amount: string, token: NetworkData) => void;
}) => {
  const [quickBuyTokens] = useLocalStorage<QuickBuyTokensLocalStorage[]>('quick-buy-tokens', []);
  const [selectedAmount, setSelectedAmount] = useState(quickBuyTokens.some(_token => _token.chainId === token.chainId) ? quickBuyTokens.find(_token => _token.chainId === token.chainId).amount : '');
  const _handleInputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue === '') {
      setSelectedAmount('');
      return;
    }
    let formattedValue = inputValue.replace(/[^0-9.]/g, '');
    const dotIndex = formattedValue.indexOf('.');
    if (dotIndex !== -1) {
      formattedValue = formattedValue.slice(0, dotIndex + 1) + formattedValue.slice(dotIndex + 1).replace(/\./g, '');
    }
    if (dotIndex !== -1 && formattedValue.length > dotIndex + 4) {
      formattedValue = formattedValue.slice(0, dotIndex + 4);
    }
    if (formattedValue.startsWith('.')) {
      formattedValue = '0' + formattedValue;
    }
    setSelectedAmount(formattedValue);
  };
  return <div className='grid gap-6 p-2' data-sentry-component="TokenSetUp" data-sentry-source-file="QuickBuyController.tsx">
      <Button variant='blue' onClick={() => setStage(QuickBuyStages.balances)} className='flex gap-2 items-center place-self-start rounded-full w-[84px] h-[31px] py-[2px]' data-sentry-element="Button" data-sentry-source-file="QuickBuyController.tsx">
        <ChevronLeft className='w-[16px] h-[16px]' data-sentry-element="ChevronLeft" data-sentry-source-file="QuickBuyController.tsx" />
        <p className='text-[14px] leading-5'>Back</p>
      </Button>
      <p className=''>{token.networkName} Set-Up</p>
      <div className='flex gap-2 items-center border border-genius-blue w-11/12 px-2 rounded-full place-self-center'>
        <div className='flex gap-2 items-center w-1/3'>
          <ImageWithFallback src={token.networkLogo} width={20} height={20} alt={`${token.networkName}-logo`} className='h-[20px] w-[20px]' data-sentry-element="ImageWithFallback" data-sentry-source-file="QuickBuyController.tsx" />
          <p>{token.ticker}</p>
        </div>
        <Input type='text' maxLength={5} placeholder='0.00' className='border-none text-right' value={selectedAmount} onChange={e => _handleInputOnChange(e)} data-sentry-element="Input" data-sentry-source-file="QuickBuyController.tsx" />
      </div>
      <div className='flex gap-2 place-self-center'>
        <p>{token.balance}</p>
        <p>{token.ticker}</p>
        <p className='opacity-50'>Available</p>
      </div>
      <div className='flex items-center justify-self-center mb-4'>
        <ToggleGroup type='single' value={selectedAmount} onValueChange={setSelectedAmount} className='gap-4' data-sentry-element="ToggleGroup" data-sentry-source-file="QuickBuyController.tsx">
          {['0.5', '1', '2', '5'].map(value => <ToggleGroupItem key={value} value={value} disabled={value === selectedAmount} className='bg-genius-blue text-genius-cream
             disabled:opacity-100 rounded-full h-[31px] w-[61px] text-[14px] data-[state=on]:bg-genius-blue flex gap-2 items-center'>
              <ImageWithFallback src={token.networkLogo} width={20} height={20} alt={`${token.networkName}-logo`} className='h-[20px] w-[20px]' />
              <p>{value}</p>
            </ToggleGroupItem>)}
        </ToggleGroup>
      </div>
      <Button variant='pink' className='rounded-full' onClick={() => onSetUpToken(selectedAmount, token)} data-sentry-element="Button" data-sentry-source-file="QuickBuyController.tsx">
        Save
      </Button>
    </div>;
};
const QuickBuyController = () => {
  const [stage, setStage] = useState<QuickBuyStages>(QuickBuyStages.balances);
  const [openMenu, setOpenMenu] = useState(false);
  const [quickBuyTokens, setQuickBuyTokens] = useLocalStorage<QuickBuyTokensLocalStorage[]>('quick-buy-tokens', []);
  const [selectedTokenToSetUp, setSelectedTokenToSetUp] = useState<(NetworkData & {
    balance: string | number;
  }) | undefined>(undefined);
  const {
    PKP
  } = useSession();
  const {
    data: assetData,
    isLoading: assetDataIsLoading
  } = useERC20Balance({
    evmAddresses: [PKP?.additionalEthAddress],
    solanaAddresses: [PKP?.solanaAddress],
    useGeniusAPI: true
  });
  let networks: NetworkData[] = [...ACTIVE_NETWORKS.map(x => ({
    networkName: V2Networks[x].name,
    chainId: x,
    networkLogo: NETWORK_LOGOS[x],
    ticker: V2Networks[x].nativeCurrency
  }))];
  const _nativeTokens = useMemo(() => {
    if (!assetData) return;
    const _native = assetData.balances.filter(token => token.native_token);
    const _result = _native.map(token => {
      return {
        ...token,
        formatted_balance: token.formatted_balance.toString().length > 4 ? token.formatted_balance.toFixed(4) : token.formatted_balance
      };
    });
    return _result;
  }, [assetData]);
  const _handleOpenChange = () => {
    setOpenMenu(!openMenu);
  };
  const _handleOnSelectToken = (value: NetworkData) => {
    console.log('_handleOnSelectToken', {
      value
    });
  };
  const _handleSetTokenConfig = (value: NetworkData) => {
    console.log('_handleSetTokenConfig', {
      value
    });
    setSelectedTokenToSetUp({
      ...value,
      balance: _nativeTokens.find(token => token.network === value.chainId).formatted_balance
    });
    if (stage !== QuickBuyStages.setUp) setStage(QuickBuyStages.setUp);
    return;
  };
  const _onSetUpToken = (amount: string, token: NetworkData) => {
    console.log('onSetUpToken', {
      amount,
      token
    });
    const existingTokenIndex = quickBuyTokens.findIndex(quickBuyToken => quickBuyToken.chainId === token.chainId);
    if (existingTokenIndex >= 0) {
      const updatedQuickBuyTokens = [...quickBuyTokens];
      updatedQuickBuyTokens[existingTokenIndex].amount = amount;
      setQuickBuyTokens(updatedQuickBuyTokens);
    } else {
      setQuickBuyTokens(prev => [...prev, {
        ...token,
        amount
      }]);
    }
    if (stage !== QuickBuyStages.balances) setStage(QuickBuyStages.balances);
    return;
  };
  if (!PKP) return;
  return <div className='flex items-center gap-2' data-sentry-component="QuickBuyController" data-sentry-source-file="QuickBuyController.tsx">
      <p>SOL Balance:</p>
      <p>5.6</p>
      <DropdownMenu open={openMenu} onOpenChange={_handleOpenChange} data-sentry-element="DropdownMenu" data-sentry-source-file="QuickBuyController.tsx">
        <DropdownMenuTrigger data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="QuickBuyController.tsx">
          <Button variant='pink' className='rounded-full w-[71px] h-[32px] px-[14px] py-[6px]' data-sentry-element="Button" data-sentry-source-file="QuickBuyController.tsx">
            0.03
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='min-w-[393px] rounded-[20px] border-genius-blue bg-genius-indigo w-[154px] p-2' data-sentry-element="DropdownMenuContent" data-sentry-source-file="QuickBuyController.tsx">
          {stage === QuickBuyStages.setUp && selectedTokenToSetUp && <TokenSetUp onSetUpToken={_onSetUpToken} token={selectedTokenToSetUp} setStage={setStage} />}
          {stage === QuickBuyStages.balances && <>
              <DropdownMenuLabel className='text-[20px] text-genius-cream leading-5 mb-2'>
                Quick Buy
              </DropdownMenuLabel>
              {networks.map(networkItem => <DropdownMenuItem key={networkItem.chainId} className='hover:bg-genius-blue/20 justify-between' onSelect={e => e.preventDefault()}>
                  <div className='flex gap-2 items-center'>
                    <ImageWithFallback src={networkItem.networkLogo} width={20} height={20} alt={`${networkItem.networkName}-logo`} className='h-[20px] w-[20px]' />
                    <div className='grid'>
                      <p>{networkItem.networkName}</p>
                      {_nativeTokens && <div className='flex gap-1 text-xs opacity-50'>
                          {_nativeTokens.some(token => token.network === networkItem.chainId) ? <>
                              <p>
                                {_nativeTokens.find(token => token.network === networkItem.chainId).formatted_balance}
                              </p>
                              <p>
                                {_nativeTokens.find(token => token.network === networkItem.chainId).contract_ticker_symbol}
                              </p>
                              <p>Available</p>
                            </> : <p>
                              Buy {networkItem.ticker} on {networkItem.networkName}
                            </p>}
                        </div>}
                    </div>
                  </div>

                  <div className='flex gap-2 items-center'>
                    <Button disabled={_nativeTokens && !_nativeTokens.some(token => token.network === networkItem.chainId)} variant='blue' className='rounded-full w-[80px] h-[39px] px-[14px] py-[6px]' onClick={() => quickBuyTokens.some(token => token.chainId === networkItem.chainId) ? _handleOnSelectToken(networkItem) : _handleSetTokenConfig(networkItem)}>
                      {quickBuyTokens.some(token => token.chainId === networkItem.chainId) ? quickBuyTokens.find(token => token.chainId === networkItem.chainId).amount : 'Set Up'}
                    </Button>
                    <Button variant='ghost' className='border border-genius-blue rounded-full w-[30px] h-[30px] p-[2px]' onClick={() => _handleSetTokenConfig(networkItem)}>
                      <Settings className='opacity-50 w-[15px] h-[15px]' />
                    </Button>
                  </div>
                </DropdownMenuItem>)}
            </>}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>;
};
export default QuickBuyController;